import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, from, map, Observable, switchMap } from 'rxjs';
import { environment } from '@env';
import {
  FirestoreData,
  GameInfo,
  GameStarted,
  Success,
  Team,
  TeamInfo,
  GameState,
  ChangeTeam,
  TeamAnswer,
  AvailableEventCount,
  ChangedQuestionsInterface,
} from '@core/models';
import { isDevMode } from '@angular/core';
import { FirebaseService } from '@core/services/firebase.service';
import { GameStartedResponse } from '@core/models/game/game-started-response.interface';
import { RoundAnswers } from '@core/models/game/round-answers.interface';
import * as _ from 'lodash';
import {
  Firestore,
  writeBatch,
  doc,
  WithFieldValue,
  DocumentData,
  setDoc,
} from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class GameApiService {
  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService,
    private firestore: Firestore,
  ) {}

  public getAvailableEventCount(): Observable<AvailableEventCount> {
    return this.http.get<{
      available_event_count: number;
      subscription_name: string;
    }>(`${environment.apiUrl}users/available-event-count`);
  }

  public gameStarted(data: GameStarted): Observable<GameStartedResponse> {
    return this.http
      .post<GameInfo>(`${environment.apiUrl}events/start`, data)
      .pipe(switchMap(() => this.firebaseService.startGame(data)));
  }

  public finishGame(
    eventId: string,
    gameCode: string,
    gameTime: string,
    host_id: string,
    location_id: string,
    player_count: number,
    team_count: number,
  ) {
    return forkJoin([
      this.http.post<GameInfo>(`${environment.apiUrl}events/finish`, {
        event_id: eventId,
        game_time_elapsed: gameTime || '00:00:00',
      }),
      this.firebaseService.finishGame(
        eventId,
        gameCode,
        host_id,
        location_id,
        player_count,
        team_count,
      ),
    ]);
  }

  public getLeaderboardTeams(code: string): Observable<Team[]> {
    return this.firebaseService.getLeaderboard(code);
  }

  public changedLeaderboardTeam(
    teamData: Team[],
    gameCode: string,
  ): Observable<void> {
    return this.firebaseService.changedLeaderboardTeam(teamData, gameCode);
  }

  public getAcceptedQuestions(
    roundId: string,
    gameCode: string,
  ): Observable<ChangedQuestionsInterface> {
    return this.firebaseService.getAcceptedQuestions(roundId, gameCode);
  }

  public getTeamAnswers(
    data: { round_id: string; team_id: string },
    game_code: string,
  ): Observable<TeamAnswer[]> {
    return this.firebaseService.getTeamAnswers(
      data.round_id,
      data.team_id,
      game_code,
    );
  }

  public getRoundAnswers(round_id: string, code: string) {
    return this.firebaseService.getRoundAnswers(round_id, code);
  }

  public getTeamMembers(teamId: string, code: string): Observable<TeamInfo> {
    return this.firebaseService.getTeamMembers(teamId, code);
  }

  public submitQuestionAnswers(
    data: ChangedQuestionsInterface,
    playerAnswers: RoundAnswers,
    gameCode: string,
    team_id: string = null,
  ): Observable<Success> {
    const batch = writeBatch(this.firestore);

    return this.firebaseService.getAllTeams(gameCode).pipe(
      switchMap((teams) => {
        for (const roundId in data) {
          for (const questionId in data[roundId]) {
            const correctAnswers: string | string[] =
              data[roundId][questionId].answers ?? [];
            const questionTypeId = data[roundId][questionId].questionTypeId;
            const answers = team_id //TODO: filter answers before
              ? playerAnswers[roundId][questionId].filter(
                  (ans) => ans.team_id == team_id,
                )
              : playerAnswers[roundId][questionId];

            for (const playerAnswer of answers || []) {
              let points = 0;

              switch (questionTypeId) {
                case 9: // choose ten
                  const count = (playerAnswer.answer as string[]).filter(
                    (ans) => correctAnswers.includes(ans),
                  ).length;
                  points = count * playerAnswer.score;
                  break;
                case 8: // puzzle matching
                  const match = correctAnswers.includes(
                    playerAnswer.answer as string,
                  );
                  points = match ? playerAnswer.score : 0;
                  playerAnswer.isCorrect = match;
                  break;

                default: // input type answers
                  let isAccepted = false;
                  if (Array.isArray(correctAnswers)) {
                    isAccepted = !!correctAnswers.find((el) => {
                      return (
                        el.toLowerCase() ===
                        (playerAnswer.answer as string).toLowerCase()
                      );
                    });
                  } else if (typeof correctAnswers === 'string') {
                    isAccepted =
                      (correctAnswers as string).toLowerCase() ===
                      (playerAnswer.answer as string).toLowerCase();
                  }

                  if (isAccepted) {
                    points = playerAnswer.score;
                  } else if (playerAnswer.wager) {
                    points = -playerAnswer.score;
                  }
                  playerAnswer.isCorrect = isAccepted;
              }

              if (playerAnswer.team_id) {
                // can we remove this?

                const team = teams.find(
                  (t) => t.ref.id === playerAnswer.team_id,
                ).team;
                if (!team.questions_score) team.questions_score = {};
                const pointDiff =
                  points - (team.questions_score[roundId]?.[questionId] ?? 0);
                team.score += pointDiff;
                _.set(team.questions_score, `${roundId}.${questionId}`, points);

                if (playerAnswer?.answer_id) {
                  const gameDocRef = doc(
                    this.firestore,
                    'games',
                    gameCode.toString(),
                    'answers',
                    playerAnswer.answer_id,
                  );

                  batch.update(
                    gameDocRef,
                    playerAnswer as WithFieldValue<DocumentData>,
                  );
                } else {
                  console.error(
                    'Cannot update: answer_id is missing',
                    playerAnswer,
                  );
                }

                const correctAnswerRef = doc(
                  this.firestore,
                  'games',
                  gameCode.toString(),
                  'correct_answers',
                  questionId,
                );

                batch.set(correctAnswerRef, {
                  question_id: questionId,
                  round_id: roundId,
                  correct_answers: correctAnswers,
                });
              }
            }
          }
        }

        this.firebaseService.sortTeams(teams, batch);

        const success = { msg: 'success' };
        return from(batch.commit()).pipe(map(() => success));
      }),
    );
  }

  public saveGameState(
    data: GameState,
    code: string,
  ): Observable<Success | null> {
    return this.firebaseService.saveGameState(data, code);
  }

  public getGameState(code: string): Observable<GameState | null> {
    return this.firebaseService.getGameState(code);
  }

  public completeGame(): Observable<Success> {
    return this.http.post<Success>(
      `${environment.apiUrl}events/complete-started-events`,
      {},
    );
  }

  public async updateFirestore(
    gameCode: string,
    data: FirestoreData,
  ): Promise<void> {
    const docRef = doc(this.firestore, 'session', gameCode.toString());

    try {
      // By default, setDoc overwrites the entire document.
      // If you want to merge instead, add { merge: true } as a third argument.
      await setDoc(docRef, data);
    } catch (error) {
      console.error('Error updating document: ', error);
    }
  }

  public decreaseAvailableEventsCount(eventId: string): Observable<Success> {
    return this.http.post<Success>(
      `${environment.apiUrl}events/decrease-event-count`,
      { event_id: eventId },
    );
  }

  public approveTeamAnswers(
    roundId: string,
    eventId: string,
    isReload = false,
  ): Observable<Success> {
    const headers = {
      headers: new HttpHeaders({
        event_id: eventId,
      }),
    };
    return this.http.post<Success>(
      `${environment.apiUrl}gameplay/approve-team-answers?event_id=${eventId}`,
      {
        round_id: roundId,
        is_reload_answer: isReload,
      },
      headers,
    );
  }

  public getLeaderboardByRound(
    eventId: string,
    roundId: string,
  ): Observable<Team[]> {
    let url = environment.amazonBaseUrl;
    if (isDevMode()) {
      url = environment.amazonBaseUrlDev;
    }
    return this.http.get<Team[]>(
      `${url}${eventId}_${roundId}_leaderboard.json`,
    );
  }
}
